import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, UserPlus, Search, Users } from 'lucide-react';
import { getPublicAgents, type PublicAgent } from '../../services/agentService';
import { castAgentAsCharacter, type Character } from '../../services/characterService';
import CastingCarousel from './CastingCarousel';

/**
 * CAST FROM THE AGENCY — pick a roster agent as a character instead of starting from an empty cast list.
 * Their vetted portrait becomes the locked face and their voice the locked voice, so the existing consistency
 * chain (reference frame → reference-to-video) works unchanged.
 *
 * The empty-cast state was the single biggest drag on time-to-first-video: a creator had to invent, describe and
 * generate someone before they could shoot anything. This turns that into one tap.
 *
 * W2d — the WHOLE roster is the acting pool, not just the media crew. An agent that no longer headlines as a chat
 * specialist still has a face, a bio and a personality — exactly an actor's portfolio — so the analyst-era roster
 * converts into casting DEPTH at zero new-content cost, and no agent is dead inventory in the narrowed product.
 *
 * CASTABILITY: the server casts from an agent's vetted PORTRAIT (gallery image or avatar) and rejects anyone
 * without one, so this surface offers only agents that will actually cast — showing a face that then errors is
 * worse than not showing it. Search spans the full castable roster (name/role/personality), because a creator
 * looking for "a stern older man" is searching for a character, not scanning a list.
 *
 * Presentational-ish (SteveJobs layering): fetches its own list, but hands the created character back via callback.
 */
const CastFromCrew: React.FC<{
  seriesId?: string | null;
  /** Ids already cast, so we don't offer a duplicate. */
  castNames?: string[];
  onCast: (c: Character) => void;
}> = ({ seriesId, castNames = [], onCast }) => {
  const [agents, setAgents] = useState<PublicAgent[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  // RF2 — the agent currently being AUDITIONED (large frame + bio + their other photos). The rail answers
  // "who is available"; this answers "is this the right actor", which is the decision that actually matters
  // when a face has to carry 60 episodes.
  const [auditionId, setAuditionId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getPublicAgents()
      .then((list) => { if (alive) setAgents(list); })
      .catch(() => { if (alive) setAgents([]); });
    return () => { alive = false; };
  }, []);

  // Only agents the server can actually cast (it needs a vetted portrait) are offered — see the note above.
  // Within those, search spans the whole castable roster so depth of cast is reachable, not just the first few.
  const castable = useMemo(() => (agents || []).filter((a) => a.avatar_image), [agents]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return castable;
    return castable.filter((a) => `${a.name} ${a.persona} ${a.description} ${a.personality} ${a.domain || ''}`.toLowerCase().includes(q));
  }, [castable, query]);

  // The rail stays tight by default; searching or expanding reveals the rest of the agency.
  const RAIL_LIMIT = 12;
  const visible = query.trim() || showAll ? matches : matches.slice(0, RAIL_LIMIT);

  // The audition follows the FILTERED list, so it always shows someone the creator can actually see on the rail.
  const auditionIndex = auditionId ? matches.findIndex((a) => a.id === auditionId) : -1;
  const audition = auditionIndex >= 0 ? matches[auditionIndex] : null;

  const cast = async (a: PublicAgent) => {
    setError('');
    setBusy(a.id);
    try {
      onCast(await castAgentAsCharacter(a.id, seriesId ?? null));
      setAuditionId(null); // decision made — return to the rail rather than leaving the audition open
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cast that specialist');
    } finally {
      setBusy(null);
    }
  };

  if (agents === null) {
    return <p className="px-1 py-2 text-[11px] text-secondary-400">Loading the agency…</p>;
  }
  if (!agents.length) {
    return <p className="px-1 py-2 text-[11px] text-secondary-400">No specialists available to cast yet.</p>;
  }

  return (
    <div>
      <p className="mb-2 px-0.5 text-[11px] text-secondary-500 dark:text-secondary-400">
        Cast someone from the agency — their face and voice stay locked across every shot.
      </p>

      {/* Search the whole roster by name, role or personality — casting is a character search, not a list scan. */}
      <label className="relative mb-2 block">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-secondary-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the agency — a name, a role, a personality"
          aria-label="Search the agency"
          className="w-full rounded-lg border border-secondary-200 bg-white py-1.5 pl-7 pr-2 text-[11px] text-secondary-900 placeholder:text-secondary-400 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white"
        />
      </label>

      {visible.length === 0 ? (
        <p className="px-0.5 py-2 text-[11px] text-secondary-400">Nobody in the agency matches that.</p>
      ) : (
        /* A scrollable face rail: browsing the roster shouldn't push the rest of the panel off screen. */
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          {visible.map((a) => {
            const already = castNames.includes(a.name);
            return (
              <button
                key={a.id}
                type="button"
                disabled={already || !!busy}
                // RF2: a tap AUDITIONS rather than casting outright — casting a face for a whole series off a
                // 56px thumbnail is a decision the UI should not let a creator make by accident.
                onClick={() => setAuditionId(a.id === auditionId ? null : a.id)}
                aria-expanded={a.id === auditionId}
                title={already ? `${a.name} is already in the cast` : `See ${a.name} — ${a.persona}`}
                className={`group flex w-16 shrink-0 flex-col items-center gap-1 disabled:opacity-40 ${a.id === auditionId ? 'opacity-100' : ''}`}
              >
                <span className={`relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border-2 bg-secondary-100 transition-colors dark:bg-secondary-800 ${a.id === auditionId ? 'border-secondary-900 dark:border-white' : 'border-transparent group-hover:border-secondary-900 dark:group-hover:border-white'}`}>
                  <img src={a.avatar_image || ''} alt={a.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                  {busy === a.id && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader2 className="h-4 w-4 animate-spin text-white" />
                    </span>
                  )}
                  {already && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-[11px] font-medium text-white">cast</span>
                  )}
                </span>
                <span className="w-full truncate text-center text-[11px] text-secondary-600 dark:text-secondary-300">{a.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* THE AUDITION — a tapped face opens at size, with their other photos and who they are, and ONE
          deliberate Cast action. Comparing two actors is a tap on the rail above: the faces ARE the person
          axis, so the audition itself only ever pages the photos of the actor it is showing. */}
      {audition && (
        <div className="mt-3">
          <CastingCarousel
            agent={audition}
            alreadyCast={castNames.includes(audition.name)}
            hiring={busy === audition.id}
            onHire={cast}
          />
        </div>
      )}

      {/* The rest of the agency is one tap away rather than hidden — depth of cast is a feature. */}
      {!query.trim() && !showAll && matches.length > RAIL_LIMIT && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-1.5 flex items-center gap-1 px-0.5 text-[11px] text-secondary-500 hover:text-secondary-900 dark:text-secondary-400 dark:hover:text-white"
        >
          <Users className="h-3 w-3" /> See all {matches.length} in the agency
        </button>
      )}

      {error && <p className="mt-1 px-0.5 text-[11px] text-secondary-500 dark:text-secondary-400">{error}</p>}
      <p className="mt-1.5 flex items-center gap-1 px-0.5 text-[11px] text-secondary-400">
        <UserPlus className="h-3 w-3" /> …or build an original character below.
      </p>
    </div>
  );
};

export default CastFromCrew;
